// ====================================================
// src/components/block/liveStreamManagement/videoEffects/AvatarRenderer.ts
//
// Rendu des modèles 3D "Avatar" choisis (cf. AvatarModels.ts) — DEUX familles
// INDÉPENDANTES et cumulables (cf. AvatarEffect, deux sélecteurs séparés) : un
// modèle "visage" ancré à CHAQUE visage suivi par FaceTracker.ts (faceSlots), et un
// modèle "mains" ancré à CHAQUE main suivie par HandTracker.ts (handSlots) —
// plusieurs personnes/mains dans le cadre reçoivent chacune leur propre instance
// (cf. AnchorSlot). Rendu WebGL vers UN SEUL canvas hors-écran partagé (jamais
// attaché au DOM), composé ensuite par-dessus la frame via ctx.drawImage dans
// AvatarEffect.composite() — même principe que le canvas de composition régie
// (useCanvasPipeline.ts) : un contexte WebGL séparé, jamais mélangé directement au
// canvas 2D de sortie.
//
// Ancrage (modèle "visage") : PAS la matrice de transformation de MediaPipe
// (facialTransformationMatrixes, déjà capturée par FaceTracker.ts) — sa convention
// caméra/échelle n'est pas assez sûre à reproduire sans banc de test visuel.
// Position, échelle ET orientation complète (roulis/tangage/lacet) sont calculés
// directement depuis les repères du visage — roulis validé en régie réelle,
// tangage/lacet construits sur la même méthode (profondeur relative des repères)
// mais pas encore confirmés — cf. updateFaceSlotTransform() pour le détail.
//
// Modèle "mains" — deux traitements possibles (cf. AvatarModelDef) :
//   - rigide (updateHandSlotTransform, ex. Bague) : un seul bloc, position/échelle/
//     roulis seulement — aucun repère de profondeur fiable équivalent au menton/
//     front du visage n'existe sur la main pour un lacet/tangage.
//   - rig articulé (updateHandRigTransform, cf. handRigBuild, ex. "Main (test)") :
//     CHAQUE phalange (os + articulation) est repositionnée individuellement depuis
//     les 21 repères réels de la main, sans bloc rigide ni targetRatio — la main
//     suit donc les doigts un par un, comme les morph targets du visage suivent
//     les expressions.
//
// Détection perdue à une rotation trop marquée : MediaPipe (visage ET main) ne
// détecte plus rien au-delà d'un certain angle — limite du modèle, pas du code.
// render() TIENT la dernière pose connue de chaque slot une COURTE fenêtre (cf.
// ANCHOR_HOLD_MS, mergeWithHeldAnchors) plutôt que de faire disparaître l'avatar dès
// la première frame ratée (un clignotement bref, une frame ratée) — mais PAS
// indéfiniment (essayé, explicitement écarté : "doivent disparaître si tous les
// points de tracking disparaissent, même s'ils y étaient avant") : passé ce délai
// sans nouvelle détection réelle, l'avatar disparaît bel et bien.
// ====================================================

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { FaceTrackingResult, FaceLandmarkPoint, getBlendshapeScore } from './FaceTracker';
import { HandTrackingResult, HandLandmarkPoint, isPersonLeftHand } from './HandTracker';
import { AvatarModelDef, AvatarAnchorKind, getAvatarModelDef, DecorationTransform, DEFAULT_DECORATION_TRANSFORM, GENERIC_HEAD_HEIGHT } from './AvatarModels';
import { HandRigParts, HAND_RIG_WRIST_INDEX, HAND_RIG_BONES } from './AvatarProceduralModels';

// Repères stables du maillage 468/478 points de MediaPipe FaceLandmarker — mêmes
// indices utilisés par la quasi-totalité des implémentations FaceMesh.
const LEFT_EYE_OUTER  = 33;
const RIGHT_EYE_OUTER = 263;
const NOSE_BRIDGE     = 168; // entre les sourcils — ancre plus stable que la pointe du nez
const CHIN            = 152; // pour le tangage, avec NOSE_BRIDGE comme haut du repère vertical
const FOREHEAD_TOP    = 10;  // haut du front, à la lisière des cheveux — cf. computeAnchorPoint('aboveHead')

// Paupières + coins de chaque œil — pour calculer son ouverture directement par la
// géométrie (cf. computeEyeClosedness, formule EAR standard à 6 points — Soukupová &
// Čech), en complément du score de blendshape MediaPipe 'eyeBlinkLeft'/'eyeBlinkRight'
// : ce dernier sépare mal un clin d'œil (un seul côté fermé) d'un clignement des deux
// yeux, le classificateur semblant surtout calibré sur ce dernier cas. LEFT_EYE_OUTER/
// RIGHT_EYE_OUTER déjà définis ci-dessus servent aussi de coin externe ici.
//
// Un seul point haut/bas par œil (essayé d'abord) s'est révélé nettement moins
// sensible à droite qu'à gauche en régie réelle (logs : l'œil droit fermé ne
// dépassait jamais ~0.4, l'œil gauche atteignait 1.00) — la paire de repères choisie
// pour un côté n'est pas forcément aussi "profonde" dans la paupière que celle de
// l'autre, le maillage MediaPipe n'étant pas garanti parfaitement symétrique en
// numérotation. La formule à 2 mesures verticales moyennées par œil (au lieu d'une
// seule) est le standard établi pour cette raison — plus robuste, pas propre à un
// modèle en particulier.
const LEFT_EYE_INNER    = 133;
const LEFT_EYE_UPPER_1  = 160;
const LEFT_EYE_UPPER_2  = 158;
const LEFT_EYE_LOWER_1  = 144;
const LEFT_EYE_LOWER_2  = 153;
const RIGHT_EYE_INNER   = 362;
const RIGHT_EYE_UPPER_1 = 387;
const RIGHT_EYE_UPPER_2 = 385;
const RIGHT_EYE_LOWER_1 = 373;
const RIGHT_EYE_LOWER_2 = 380;

// Ratio (écart haut/bas de la paupière ÷ largeur de l'œil) typique œil grand ouvert
// vs fermé — non calibré sur un vrai visage (comme SMOOTHING), à ajuster au test :
// si l'œil semble se fermer trop tôt/trop tard, resserrer/écarter ces deux bornes.
// EAR_CLOSED remonté de 0.10 : un œil réellement fermé ne va souvent pas aussi bas
// (le maillage MediaPipe garde un petit espace résiduel même paupière baissée), donc
// 0.10 ne se voyait quasiment jamais atteint et l'œil ne faisait QUE plisser (constaté
// en régie réelle) sans jamais se fermer complètement.
const EAR_OPEN   = 0.30;
const EAR_CLOSED = 0.18;

// Écart minimal entre les deux yeux (cf. computeEyeClosedness) pour considérer que
// c'est un clin d'œil et pas du bruit — sans cette garde, fermer UN œil faisait
// plisser les DEUX (le visage entier bouge un peu pendant le geste, l'œil resté
// ouvert obtient donc lui aussi un score EAR légèrement élevé). Non calibré, à
// ajuster au test : trop bas, le "plisse les 2" revient ; trop haut, le clin d'œil
// ne déclenche plus rien du tout.
const EYE_ASYMMETRY_MARGIN = 0.25;

// Console de la page régie (F12 → Console) pendant le test du clin d'œil — mettre à
// false une fois le réglage terminé. Throttlé (cf. lastEyeBlinkDebugLogAt) pour ne
// pas inonder la console à 25-30 logs/seconde.
const DEBUG_EYE_BLINK = true;
let lastEyeBlinkDebugLogAt = 0;

// Idem pour la position/orientation de la tête (cf. updateFaceSlotTransform) — mettre
// à false une fois le réglage terminé.
const DEBUG_HEAD_TRACKING = true;
let lastHeadTrackingDebugLogAt = 0;

// Idem pour le calcul d'échelle d'un modèle/décoration 'face'/'eyes'/'aboveHead' (cf.
// updateFaceSlotTransform) — mettre à false une fois le réglage terminé.
const DEBUG_SCALE = true;
let lastScaleDebugLogAt = 0;

// Idem pour le décalage local calibré d'UNE décoration (cf. DecorationTransform,
// updateFaceSlotTransform) — "la couronne trop sur le devant par rapport à ce qui a
// été réglé" (constaté en régie réelle) : regarder si c'est le décalage LOCAL calibré
// (avant rotation) qui a déjà le mauvais Z, ou la ROTATION par pitch/yaw/roll qui fait
// déborder une partie de l'offset Y (grand pour une couronne) dans l'axe Z au moindre
// mouvement de tête — plutôt que deviner encore un correctif. À retirer une fois le
// réglage terminé.
const DEBUG_DECORATION_TRANSFORM = true;
// PAR décoration (pas une seule variable partagée, corrigé — sinon quand plusieurs
// décorations sont actives, la première traitée à chaque frame "vole" le throttle et
// les autres ne se logguent jamais : constaté, seul "glasses" apparaissait, jamais
// "wig" pourtant actif en même temps).
const lastDecorationDebugLogAtById = new Map<string, number>();

// ⚠️ PAS de constante globale ici (une valeur unique — essayée d'abord, ex. 0.85 —
// ne peut pas convenir à la fois à Visage/Renard/Poisson/générique, qui n'ont pas la
// même profondeur réelle ; constaté : encore décalé une fois appliqué à un vrai modèle
// bien plus profond qu'une simple sphère). cf. DecorationTransform.centerDepthOffset —
// MESURÉE sur le modèle réellement affiché au moment de CETTE calibration (cf.
// DecorationCalibrationModal, attachReference) et sauvegardée avec le reste : décale
// l'ancrage réel (racine du nez, pts[NOSE_BRIDGE]) vers le CENTRE DU VOLUME affiché,
// pour correspondre EXACTEMENT au repère du panneau (centre géométrique, pas la
// surface avant — demandé explicitement : "je veux que la régie fasse pareil").

// MediaPipe n'a AUCUN repère sur le crâne/les cheveux (le maillage s'arrête à la
// lisière du front) — 'aboveHead' extrapole donc au-delà du front, dans le
// prolongement du vecteur menton→front, sur cette fraction de la hauteur du visage.
const ABOVE_HEAD_EXTRAPOLATION = 0.55;

// Repères stables du maillage 21 points de MediaPipe HandLandmarker (mêmes indices
// pour toute implémentation MediaPipe Hands) — utilisés pour l'ancrage 'hand'.
const WRIST           = 0;
const INDEX_FINGER_MCP = 5;  // base de l'index — avec PINKY_MCP, donne la largeur de paume
const MIDDLE_FINGER_MCP = 9; // avec WRIST, donne l'axe "vertical" de la main pour le roulis
const PINKY_MCP        = 17;

export type FingerName = 'thumb' | 'index' | 'middle' | 'ring' | 'pinky';

/** Repère MediaPipe HandLandmarker (articulation médiane — PIP, IP pour le pouce)
 * pour CHAQUE doigt — sert de point d'ancrage EXACT à une décoration ancrée 'hand'
 * qui veut suivre un doigt précis (ex. Bague, cf. AvatarEffect, HandDecorationOptions.
 * fingerLandmarkIndex) plutôt que le dos générique de la main (cf.
 * computeHandAnchorPoint). Non calibré — une bague se porte plutôt à la base du
 * doigt (MCP) ou entre MCP et PIP ; le PIP est un repère simple et stable, à ajuster
 * si besoin une fois vu en régie réelle. */
export const FINGER_PIP_LANDMARK: Record<FingerName, number> = {
  thumb: 3, index: 6, middle: 10, ring: 14, pinky: 18,
};

// MediaPipe FaceLandmarker/HandLandmarker sont entraînés sur des visages/mains
// quasi de face — au-delà d'un certain angle de rotation, ils ne détectent tout
// simplement plus rien (limite du modèle, rien à régler côté code). Plutôt que de
// faire disparaître l'avatar dès la première frame ratée, on TIENT sa dernière
// position/orientation connue pendant cette fenêtre — cf. mergeWithHeldAnchors.
// Passé ce délai sans nouvelle détection réelle, le visage/la main est considéré(e)
// vraiment parti(e) et disparaît — demandé explicitement : "doivent disparaître si
// tous les points de tracking disparaissent, même s'ils y étaient avant" (un essai
// de tenue INDÉFINIE, sans expiration, a été explicitement écarté).
//
// La VALEUR d'origine (600ms) s'est révélée trop courte : un mouvement de tête
// naturel (pencher, tourner) dépasse couramment cette limite de détection pendant
// PLUS de 600ms alors que le visage est manifestement toujours devant la caméra —
// l'avatar disparaissait donc en plein mouvement normal, pas juste sur une vraie
// perte (constaté explicitement : "tu vires le visage... alors qu'il reste
// manifestement des points de tracking"). Remontée à une fenêtre bien plus généreuse
// pour couvrir un mouvement de tête naturel, tout en disparaissant encore pour une
// absence qui dure vraiment (personne hors cadre). Non calibré sur un vrai
// mouvement — à ajuster au test si encore trop court/long.
const ANCHOR_HOLD_MS = 3000;

// Choix arbitraires mais cohérents entre eux : FOV et distance s'annulent dans le
// calcul de position/échelle (cf. render()), n'importe quelle paire aurait convenu.
const CAMERA_FOV_DEG  = 50;
const CAMERA_DISTANCE = 5;

// Rayon d'un "os" du rig de main (cf. AvatarModels.ts 'hand-rig', updateHandRigTransform),
// en multiples de la largeur de paume mesurée — non calibré, à ajuster au test.
const HAND_RIG_BONE_RADIUS_RATIO = 0.13;
// Simple distinction visuelle main gauche/droite pendant le test (cf. HandTracker.ts,
// handedness) — PAS une géométrie différente, juste une teinte, pour vérifier que
// chaque main détectée reçoit bien sa propre couleur. ⚠️ MediaPipe étiquette Left/
// Right de façon anatomique, donc en miroir par rapport à l'écran d'une caméra
// selfie — lequel des deux s'affiche à quel endroit n'est pas encore confirmé en
// régie réelle.
const HAND_RIG_COLOR_LEFT  = 0x93c5fd;
const HAND_RIG_COLOR_RIGHT = 0xfca5a5;
const HAND_RIG_UP_AXIS = new THREE.Vector3(0, 1, 0);

interface LoadedModel {
  /** Racine du CLONE monté dans ce renderer précis — jamais le gabarit partagé
   * lui-même (cf. ModelTemplate) : un THREE.Object3D n'a qu'un seul parent à la
   * fois, add() le reparenterait en le retirant de tout autre renderer qui
   * l'afficherait déjà. Chaque source a son propre clone, sa propre animation. */
  root: THREE.Object3D;
  size: number;
  mixer: THREE.AnimationMixer | null;
  /** Détecté automatiquement à l'instanciation (cf. instantiate()) — le PREMIER maillage
   * du clone qui porte des morph targets, et la table de correspondance entre noms de
   * blendshapes MediaPipe (cf. FaceTrackingResult.blendshapes) et leur index dans
   * `mesh.morphTargetInfluences`. Null pour tout modèle sans morph targets (poisson,
   * renard, accessoires...) — applyFaceMorphTargets ne fait alors simplement rien. */
  morphTargets: { mesh: THREE.Mesh; indexByBlendshapeName: Record<string, number> } | null;
  /** Rig "os + articulations" (cf. AvatarModelDef.handRigBuild, AvatarProceduralModels)
   * — non null UNIQUEMENT pour un modèle de main articulée, repositionné par phalange
   * chaque frame (cf. updateHandRigTransform) plutôt que comme un bloc rigide. */
  handRig: HandRigParts | null;
  /** Décalage (unités NATIVES de ce modèle, avant mise à l'échelle) entre son pivot
   * réel (local (0,0,0) — cf. `pivot` dans instantiate()) et le centre GÉOMÉTRIQUE de
   * sa boîte englobante — (0,0,0) si son pivot EST déjà son centre (ex. Poisson/Renard),
   * sinon décalé (ex. Visage/Anime, pivotés sur leur face avant : le centre est alors
   * derrière ce pivot). Sert à FUSIONNER une décoration dans ce modèle (cf.
   * AvatarRenderer.updateDecorations, "modèle fusionné") : la décoration se positionne
   * alors par rapport au VRAI centre mesuré de CE modèle précis, pas une estimation
   * générique de la profondeur d'une tête (cf. DecorationTransform.centerDepthOffset,
   * qui ne sert plus que si AUCUN modèle de remplacement n'est actif pour fusionner). */
  centerOffsetFromPivot: THREE.Vector3;
  /** Hauteur (axe Y, unités NATIVES) de la boîte englobante — PAS `size` (sa plus
   * grande dimension, potentiellement X ou Z) : sert de RÈGLE pour fusionner une
   * décoration (cf. attachDecorationToFaceModel), avec exactement la même mesure
   * (`box.getSize().y`) que DecorationCalibrationModal utilise pour recaler sa
   * référence affichée sur GENERIC_HEAD_HEIGHT — une seule et même règle des deux
   * côtés (panneau ET régie), là où `AvatarModelDef.targetRatio` (jamais vérifié sur
   * un vrai visage) en introduisait une SECONDE, divergente (constaté : lunettes
   * visiblement décalées en régie malgré un réglage centré dans le panneau). */
  heightY: number;
}

interface ModelTemplate {
  /** Gabarit partagé entre tous les renderers — jamais ajouté à une scène
   * directement, seulement cloné (cf. instantiate()). */
  scene: THREE.Object3D;
  animations: THREE.AnimationClip[];
  /** Plus grande dimension de la boîte englobante — remplace l'ancienne constante
   * nativeSize mesurée à la main, vaut pour tout modèle ajouté au registry sans
   * mesure manuelle. */
  size: number;
  /** Centre de la boîte englobante — pivot pour un ancrage 'face'/'eyes' (cf.
   * instantiate()). */
  center: THREE.Vector3;
  /** Coin bas de la boîte englobante (X/Z du centre, Y au plus bas) — pivot pour un
   * ancrage 'aboveHead' : un chapeau doit reposer par sa BASE sur le point d'ancrage,
   * pas être centré dessus (sinon la moitié dépasserait sous le point d'accroche). */
  bottomCenter: THREE.Vector3;
  /** Centre X/Y de la boîte englobante, mais Z au bord le plus proche de la caméra
   * (box.max.z — toujours le bord "avant" par construction d'un Box3, quelle que
   * soit la convention d'axes du fichier d'origine) — pivot pour AvatarModelDef.
   * depthAnchor 'front' : une tête qui REMPLACE le visage doit présenter sa face
   * avant au point d'ancrage, pas son centre volumétrique (sinon son propre relief
   * — nez, menton — ressort devant le vrai visage, cf. AvatarModels.ts). */
  frontCenter: THREE.Vector3;
  /** Hauteur (axe Y) de la boîte englobante — cf. LoadedModel.heightY. */
  heightY: number;
}

// Un chargement par id de modèle, jamais deux fois le même fichier/la même géométrie
// procédurale — partagé entre toutes les instances d'AvatarRenderer (une par source,
// cf. getAvatarRenderer plus bas). Le gabarit chargé n'est LUI-MÊME jamais monté dans
// une scène — cf. instantiate().
const templateCache = new Map<string, Promise<ModelTemplate>>();

function measureTemplate(scene: THREE.Object3D, animations: THREE.AnimationClip[]): ModelTemplate {
  const box     = new THREE.Box3().setFromObject(scene);
  const center  = box.getCenter(new THREE.Vector3());
  const sizeVec = box.getSize(new THREE.Vector3());
  const size    = Math.max(sizeVec.x, sizeVec.y, sizeVec.z) || 1;
  const bottomCenter = new THREE.Vector3(center.x, box.min.y, center.z);
  const frontCenter  = new THREE.Vector3(center.x, center.y, box.max.z);
  return { scene, animations, size, center, bottomCenter, frontCenter, heightY: sizeVec.y || 1 };
}

// Certains modèles glTF (ex. facecap.glb, cf. AvatarModels.ts) compressent leur
// géométrie (EXT_meshopt_compression) et/ou leurs textures (KHR_texture_basisu/KTX2)
// pour rester légers — GLTFLoader a besoin de ces deux décodeurs pour les lire, mais
// les attacher ne coûte rien pour un modèle qui ne les utilise pas (Poisson, Renard) :
// configuré une fois pour tout chargement distant, jamais conditionnellement.
// THREE.REVISION ('186') plutôt qu'un numéro figé en dur : reste synchronisé avec le
// `three` réellement installé (package.json), qui publie sous "0.<REVISION>.0" sur npm.
const KTX2_TRANSCODER_PATH = `https://cdn.jsdelivr.net/npm/three@0.${THREE.REVISION}.0/examples/jsm/libs/basis/`;

let ktx2Loader: KTX2Loader | null = null;

/** KTX2Loader.detectSupport() a seulement besoin d'un WebGLRenderer pour lire quels
 * formats de texture compressée le GPU sait décoder — un renderer JETABLE, dédié à
 * cette détection ponctuelle, suffit très bien : pas besoin d'attendre qu'un des
 * renderers réels (un par source, cf. AvatarRenderer) existe déjà. */
function getKtx2Loader(): KTX2Loader {
  if (!ktx2Loader) {
    ktx2Loader = new KTX2Loader().setTranscoderPath(KTX2_TRANSCODER_PATH);
    const probe = new THREE.WebGLRenderer();
    ktx2Loader.detectSupport(probe);
    probe.dispose();
  }
  return ktx2Loader;
}

function loadGltfTemplate(url: string): Promise<ModelTemplate> {
  const loader = new GLTFLoader()
    .setKTX2Loader(getKtx2Loader())
    .setMeshoptDecoder(MeshoptDecoder);
  return loader.loadAsync(url).then(gltf => measureTemplate(gltf.scene, gltf.animations));
}

function loadTemplate(def: AvatarModelDef): Promise<ModelTemplate> {
  let promise = templateCache.get(def.id);
  if (!promise) {
    promise = def.build ? Promise.resolve(measureTemplate(def.build(), [])) : loadGltfTemplate(def.url!);
    templateCache.set(def.id, promise);
  }
  return promise;
}

/** Traduit le nom d'un morph target tel qu'exporté par le modèle (ex. 'eyeBlink_L',
 * convention de facecap.glb) vers le nom de blendshape ARKit standard utilisé par
 * MediaPipe (ex. 'eyeBlinkLeft', cf. FaceTracker.ts) — un modèle qui nommerait déjà
 * ses cibles à l'identique (ex. 'jawOpen') traverse cette fonction inchangé. */
function modelTargetNameToBlendshapeName(targetName: string): string {
  if (targetName.endsWith('_L')) return targetName.slice(0, -2) + 'Left';
  if (targetName.endsWith('_R')) return targetName.slice(0, -2) + 'Right';
  return targetName;
}

/** Clone un gabarit pour CE renderer (SkeletonUtils.clone — pas Object3D.clone, qui ne
 * duplique pas correctement les liaisons squelette/animation d'un maillage riggé comme
 * le Renard) et lui donne sa propre animation, indépendante des autres sources. Le
 * pivot soustrait dépend de l'ancrage du modèle (cf. AvatarModels.ts, ModelTemplate) —
 * jamais appliqué au gabarit lui-même, qui doit garder sa boîte englobante intacte
 * pour les clones suivants. */
function instantiate(template: ModelTemplate, def: AvatarModelDef): LoadedModel {
  const root = cloneSkeleton(template.scene) as THREE.Object3D;
  const pivot = def.anchor === 'aboveHead' ? template.bottomCenter
    : def.depthAnchor === 'front' ? template.frontCenter
    : template.center;
  root.position.sub(pivot);
  const centerOffsetFromPivot = template.center.clone().sub(pivot);

  // Premier maillage à morph targets trouvé dans le clone (cf. LoadedModel.morphTargets)
  // — un seul mesh en porte 52 pour facecap.glb (la tête), rien pour tout autre modèle.
  let morphTargets: LoadedModel['morphTargets'] = null;
  root.traverse(obj => {
    if (morphTargets) return;
    const mesh = obj as THREE.Mesh;
    if (mesh.isMesh && mesh.morphTargetDictionary && mesh.morphTargetInfluences) {
      const indexByBlendshapeName: Record<string, number> = {};
      for (const [targetName, index] of Object.entries(mesh.morphTargetDictionary)) {
        indexByBlendshapeName[modelTargetNameToBlendshapeName(targetName)] = index;
      }
      morphTargets = { mesh, indexByBlendshapeName };
    }
  });

  // Un modèle à morph targets est piloté EN DIRECT par le suivi du visage (cf.
  // applyFaceMorphTargets) — ses éventuels clips embarqués (comme la performance
  // enregistrée de facecap.glb, qui anime justement ces MÊMES morph targets) ne
  // doivent JAMAIS jouer : ils écraseraient nos valeurs par une animation démo sans
  // rapport avec la personne réellement suivie.
  let mixer: THREE.AnimationMixer | null = null;
  if (template.animations.length > 0 && !morphTargets) {
    mixer = new THREE.AnimationMixer(root);
    const clip = template.animations.find(a => /survey/i.test(a.name)) ?? template.animations[0];
    mixer.clipAction(clip).play();
  }

  return { root, size: template.size, mixer, morphTargets, handRig: null, centerOffsetFromPivot, heightY: template.heightY };
}

/** Charge et instancie une copie de `def` pour affichage de RÉFÉRENCE (cf.
 * DecorationCalibrationModal) — même pivot (centre/bas/avant selon l'ancrage, cf.
 * instantiate()) que celui réellement utilisé en régie par ensureSlotModel, pour
 * que calibrer une décoration par-dessus ce modèle donne le même résultat qu'en
 * régie réelle. Demandé explicitement : montrer LE modèle actuellement choisi
 * (Visage/Mains), pas une forme générique de substitution.
 *
 * Retourne null pour un modèle "rig" de main (cf. AvatarModelDef.handRigBuild,
 * updateHandRigTransform) : ses os/articulations n'ont AUCUNE pose propre — ils
 * sont entièrement repositionnés chaque frame depuis les 21 repères RÉELS d'une
 * main suivie. Construit hors de tout suivi (comme ici), ce serait un tas de
 * sphères/cylindres superposés à l'origine, pas une main reconnaissable ;
 * l'appelant retombe alors sur buildReferenceHand() (générique mais au moins
 * lisible). */
export function loadReferenceModel(def: AvatarModelDef): Promise<THREE.Object3D | null> {
  if (def.handRigBuild) return Promise.resolve(null);
  return loadTemplate(def).then(template => {
    const loaded = instantiate(template, def);
    // Le fichier glTF garde son échelle NATIVE (mètres réels pour Poisson/Renard,
    // convention propre à facecap.glb pour Visage...) — sans rapport avec l'unité
    // de calibration (distance inter-yeux = 1, cf. DecorationTransform). Même
    // relation qu'en régie réelle (cf. updateFaceSlotTransform/updateHandSlotTransform :
    // scale = worldSize/loaded.size, avec worldSize ≈ targetRatio unités quand la
    // mesure de référence — inter-yeux ou largeur de paume — vaut 1) : sans cette
    // normalisation le modèle était soit microscopique soit démesuré selon le
    // fichier, donc INVISIBLE dans le cadre du panneau (constaté : "je ne vois pas
    // le visage").
    loaded.root.scale.multiplyScalar(def.targetRatio / loaded.size);
    return loaded.root;
  });
}

/** Construit le maillage d'UNE décoration (cf. AvatarModelDef.build) avec EXACTEMENT
 * le même pivot que ce que fait réellement ensureSlotModel/instantiate() en régie
 * (bas de la boîte englobante pour 'aboveHead', face avant pour depthAnchor:'front',
 * centre volumétrique sinon) — pour DecorationCalibrationModal, qui appelait `build()`
 * directement sans ce recentrage : le repère (AxesHelper, au centre des 3 vues, sur le
 * point d'ancrage) ne correspondait alors pas à l'origine locale réelle de la
 * décoration, donc elle ne se positionnait pas comme en régie (constaté : "le repère
 * doit être centré sur le visage ou la main"). Synchrone : `build()` l'est déjà, pas
 * besoin de passer par templateCache/loadTemplate (pensés pour un gabarit partagé
 * entre plusieurs instances, inutile ici — une seule décoration, un seul panneau). */
export function buildDecorationReferenceMesh(def: AvatarModelDef): THREE.Object3D {
  const mesh = def.build!();
  const template = measureTemplate(mesh, []);
  const pivot = def.anchor === 'aboveHead' ? template.bottomCenter
    : def.depthAnchor === 'front' ? template.frontCenter
    : template.center;
  mesh.position.sub(pivot);
  return mesh;
}

/** Point du visage (coordonnées normalisées 0..1) où ancrer ce modèle — cf.
 * AvatarModelDef.anchor dans AvatarModels.ts pour ce que chaque valeur représente. */
function computeAnchorPoint(kind: AvatarAnchorKind, pts: FaceLandmarkPoint[]): FaceLandmarkPoint | null {
  const leftEye  = pts[LEFT_EYE_OUTER];
  const rightEye = pts[RIGHT_EYE_OUTER];
  if (!leftEye || !rightEye) return null;

  switch (kind) {
    case 'eyes':
      return {
        x: (leftEye.x + rightEye.x) / 2,
        y: (leftEye.y + rightEye.y) / 2,
        z: (leftEye.z + rightEye.z) / 2,
      };
    case 'aboveHead': {
      const forehead = pts[FOREHEAD_TOP];
      const chin     = pts[CHIN];
      if (!forehead || !chin) return null;
      // Extrapolation au-delà du front, dans le prolongement du vecteur menton→front
      // (cf. ABOVE_HEAD_EXTRAPOLATION) — MediaPipe n'a aucun repère sur le crâne.
      return {
        x: forehead.x + (forehead.x - chin.x) * ABOVE_HEAD_EXTRAPOLATION,
        y: forehead.y + (forehead.y - chin.y) * ABOVE_HEAD_EXTRAPOLATION,
        z: forehead.z + (forehead.z - chin.z) * ABOVE_HEAD_EXTRAPOLATION,
      };
    }
    case 'face':
    default:
      return pts[NOSE_BRIDGE] ?? null;
  }
}

/** Ouverture d'UN œil (0 = grand ouvert, 1 = fermé), calculée directement depuis ses
 * repères — formule EAR standard à 6 points (2 mesures verticales moyennées ÷
 * largeur de l'œil), cf. EAR_OPEN/EAR_CLOSED. Indépendant du score de blendshape
 * MediaPipe : sert de secours quand celui-ci ne réagit pas assez à un clin d'œil
 * (cf. applyFaceMorphTargets). */
function computeEyeClosedness(
  pts: FaceLandmarkPoint[],
  outerIdx: number, upper1Idx: number, upper2Idx: number,
  innerIdx: number, lower2Idx: number, lower1Idx: number,
): number {
  const outer = pts[outerIdx], inner = pts[innerIdx];
  const upper1 = pts[upper1Idx], lower1 = pts[lower1Idx];
  const upper2 = pts[upper2Idx], lower2 = pts[lower2Idx];
  if (!outer || !inner || !upper1 || !lower1 || !upper2 || !lower2) return 0;
  const eyeWidth = Math.hypot(inner.x - outer.x, inner.y - outer.y);
  if (eyeWidth === 0) return 0;
  const vertical1 = Math.hypot(upper1.x - lower1.x, upper1.y - lower1.y);
  const vertical2 = Math.hypot(upper2.x - lower2.x, upper2.y - lower2.y);
  const ear = (vertical1 + vertical2) / (2 * eyeWidth);
  const linear = Math.max(0, Math.min(1, (EAR_OPEN - ear) / (EAR_OPEN - EAR_CLOSED)));
  // Racine carrée plutôt que linéaire : pousse les valeurs intermédiaires vers 1 plus
  // vite (0.5 → 0.71, 0.7 → 0.84) — un œil "presque fermé" se ferme complètement à
  // l'écran plutôt que de juste plisser, quitte à déclencher un peu plus tôt.
  return Math.sqrt(linear);
}

/** Centre d'UNE main détectée (coordonnées normalisées 0..1) — centroïde du poignet
 * et des bases d'index/auriculaire, un point stable au dos/creux de la main quel
 * que soit l'écartement des doigts (contrairement à la pointe d'un doigt). */
function computeHandAnchorPoint(pts: HandLandmarkPoint[]): FaceLandmarkPoint | null {
  const wrist  = pts[WRIST];
  const index  = pts[INDEX_FINGER_MCP];
  const pinky  = pts[PINKY_MCP];
  if (!wrist || !index || !pinky) return null;
  return {
    x: (wrist.x + index.x + pinky.x) / 3,
    y: (wrist.y + index.y + pinky.y) / 3,
    z: (wrist.z + index.z + pinky.z) / 3,
  };
}

/** Réglages fins pour UNE décoration ancrée 'hand' (cf. AvatarModelDef.isDecoration,
 * AvatarRenderer.updateDecorations) — quelle(s) main(s) et quel doigt précis, plutôt
 * que toutes les mains détectées ancrées au dos de la main (comportement par défaut
 * si absent/non fourni). Générique : le renderer ne sait pas à quelle décoration
 * (ex. Bague) ces options s'appliquent, juste un id → options. */
export interface HandDecorationOptions {
  handSide?: 'left' | 'right' | 'both';
  /** Index d'un repère MediaPipe HandLandmarker (0-20) à utiliser comme point
   * d'ancrage EXACT, à la place du centre générique du dos de la main (cf.
   * computeHandAnchorPoint) — typiquement l'articulation d'UN doigt précis. */
  fingerLandmarkIndex?: number;
}

/** État propre à UN visage OU UNE main détecté(e) — chacun son clone de modèle (cf.
 * instantiate, un Object3D n'a qu'un seul parent), sa propre animation, son propre
 * lissage. Deux visages/mains affichant le MÊME modèle ne doivent jamais partager
 * leur instance. */
interface AnchorSlot {
  group: THREE.Group;
  modelId: string | null;
  loaded: LoadedModel | null;
  lastAnimTime: number;
  hasSmoothedState: boolean;
  smoothedX: number;
  smoothedY: number;
  smoothedRoll: number;
  smoothedYaw: number;
  smoothedPitch: number;
  smoothedScale: number;
  /** Lissage EMA par index de morph target (cf. LoadedModel.morphTargets, applyFace
   * MorphTargets) — une Map plutôt que des champs nommés : le nombre de morph targets
   * dépend du modèle chargé (52 pour facecap.glb, aucun pour tout autre modèle, donc
   * jamais peuplée pour eux). Vidée à chaque changement de modèle (cf. ensureSlotModel). */
  smoothedMorphTargets: Map<number, number>;
  /** Dernier repère RÉELLEMENT détecté pour ce slot (jamais un repère lui-même tenu
   * en mémoire, cf. mergeWithHeldAnchors) et l'horodatage de cette détection — sert
   * à combler une frame sans détection par la dernière position connue, tant que
   * ANCHOR_HOLD_MS n'est pas dépassé. Un seul et même slot ne porte jamais les deux
   * types à la fois (face XOR main) — le type de modelId le détermine. */
  lastKnownAnchor: FaceTrackingResult | HandTrackingResult | null;
  lastSeenAt: number;
}

function createAnchorSlot(): AnchorSlot {
  return {
    group: new THREE.Group(), modelId: null, loaded: null, lastAnimTime: 0,
    hasSmoothedState: false,
    smoothedX: 0, smoothedY: 0, smoothedRoll: 0, smoothedYaw: 0, smoothedPitch: 0, smoothedScale: 0,
    smoothedMorphTargets: new Map(),
    lastKnownAnchor: null, lastSeenAt: 0,
  };
}

// 0 = figé, 1 = aucun lissage — même valeur pour tous les slots, pas encore calibrée
// sur un vrai flux (cf. le lissage lui-même dans updateSlotTransform).
const SMOOTHING = 0.35;

export class AvatarRenderer {
  private canvas    = document.createElement('canvas');
  private renderer: THREE.WebGLRenderer | null = null;
  private scene     = new THREE.Scene();
  private camera    = new THREE.PerspectiveCamera(CAMERA_FOV_DEG, 1, 0.1, 100);
  // Deux familles INDÉPENDANTES — visage et mains peuvent être actifs en même temps
  // (cf. AvatarEffect, deux sélecteurs cumulables), chacune avec ses propres slots.
  // Un slot par visage/main détecté SIMULTANÉMENT dans sa famille — indexé par
  // position dans le tableau de repères de render(), pas par identité de personne
  // (ni FaceLandmarker ni HandLandmarker ne suivent qui est qui d'une frame à
  // l'autre, seulement combien et où). Grandit au besoin (cf. ensureSlot) ; jamais
  // retiré, seulement masqué (group.visible) quand moins de visages/mains sont
  // détectés qu'avant — évite de recréer contexte/animation à chaque fluctuation.
  private faceSlots: AnchorSlot[] = [];
  // Une réserve de slots par main : la gauche et la droite peuvent porter des modèles
  // différents (cf. render, leftHandModelId/rightHandModelId).
  private leftHandSlots: AnchorSlot[] = [];
  private rightHandSlots: AnchorSlot[] = [];
  // Une décoration (cf. AvatarModelDef.isDecoration — Couronne, Lunettes, Bague,
  // Moustache...) a un id STABLE tant qu'elle reste cochée (contrairement à
  // faceModelId/handModelId, qui peuvent changer de modèle) : son jeu de slots est
  // donc gardé PAR ID plutôt que dans un seul tableau, pour que plusieurs
  // décorations s'affichent en même temps sans se marcher dessus. Jamais purgée :
  // une décoration décochée voit juste ses slots masqués (cf. updateDecorations),
  // réutilisés si elle est recochée plus tard.
  private decorationSlots = new Map<string, AnchorSlot[]>();
  private lastWidth  = 0;
  private lastHeight = 0;

  constructor() {
    this.camera.position.set(0, 0, CAMERA_DISTANCE);
    this.camera.lookAt(0, 0, 0);
    this.scene.add(new THREE.AmbientLight(0xffffff, 1.2));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.5);
    dirLight.position.set(1, 1, 2);
    this.scene.add(dirLight);
  }

  private ensureSlot(slots: AnchorSlot[], index: number): AnchorSlot {
    while (slots.length <= index) {
      const slot = createAnchorSlot();
      this.scene.add(slot.group);
      slots.push(slot);
    }
    return slots[index];
  }

  /** Lance (si besoin) le chargement du modèle `def` pour CE slot et le monte dès
   * qu'il est prêt — appelé à chaque frame pour chaque visage/main, ne relance rien
   * tant que l'id ne change pas POUR CE SLOT précis (les autres slots ne sont pas
   * affectés, même s'ils affichent encore l'ancien modèle le temps de charger). Un
   * modèle "rig" (cf. AvatarModelDef.handRigBuild) est procédural et synchrone —
   * monté immédiatement, sans passer par templateCache/loadTemplate (pensés pour un
   * gabarit CHARGÉ, glTF ou build() statique, pas pour une structure redessinée
   * chaque frame). */
  private ensureSlotModel(slot: AnchorSlot, def: AvatarModelDef): void {
    if (slot.modelId === def.id) return;
    slot.modelId = def.id;
    slot.loaded = null; // ce slot précis retombe sur le repli (points) le temps du chargement/permutation
    slot.lastAnimTime = 0;
    slot.hasSmoothedState = false; // le nouveau modèle démarre sur sa position brute, pas l'ancienne lissée
    slot.smoothedMorphTargets.clear(); // les index de l'ancien modèle n'ont aucun sens pour le nouveau

    if (def.handRigBuild) {
      const handRig = def.handRigBuild();
      slot.group.clear();
      slot.group.add(handRig.group);
      slot.loaded = { root: handRig.group, size: 1, mixer: null, morphTargets: null, handRig, centerOffsetFromPivot: new THREE.Vector3(), heightY: 1 };
      return;
    }

    loadTemplate(def).then(template => {
      if (slot.modelId !== def.id) return; // un autre modèle a été choisi pour ce visage/cette main entre-temps
      const loaded = instantiate(template, def);
      slot.group.clear();
      slot.group.add(loaded.root);
      slot.loaded = loaded;
    }).catch(err => console.warn(`[AvatarRenderer] échec de chargement du modèle "${def.id}" :`, err));
  }

  private ensureRenderer(width: number, height: number): THREE.WebGLRenderer | null {
    if (!this.renderer) {
      try {
        this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: true });
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        this.renderer.setClearColor(0x000000, 0); // fond transparent — posé par-dessus la frame ensuite
      } catch (err) {
        console.warn('[AvatarRenderer] WebGL indisponible :', err);
        return null;
      }
    }
    if (this.lastWidth !== width || this.lastHeight !== height) {
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.lastWidth  = width;
      this.lastHeight = height;
    }
    return this.renderer;
  }

  /** Met à jour position/rotation/échelle/animation d'UN slot pour CE visage — cf.
   * render() pour la boucle sur tous les visages détectés. Rien à retourner : agit
   * directement sur slot.group, déjà dans la scène (cf. ensureSlot). */
  private updateFaceSlotTransform(slot: AnchorSlot, face: FaceTrackingResult, width: number, height: number, def: AvatarModelDef, scaleMultiplier: number, decorationTransform?: DecorationTransform): void {
    if (!slot.loaded) return;
    const pts        = face.landmarks;
    const leftEye    = pts[LEFT_EYE_OUTER];
    const rightEye   = pts[RIGHT_EYE_OUTER];
    const noseBridge = pts[NOSE_BRIDGE]; // repère fixe pour le tangage, indépendant de l'ancrage du modèle
    const chin       = pts[CHIN];
    // Une DÉCORATION (decorationTransform fourni) ancre TOUJOURS au même point —
    // 'face' (racine du nez, repère le plus stable) — quel que soit son propre
    // AvatarModelDef.anchor ('aboveHead' pour une couronne, 'eyes' pour des lunettes...) :
    // c'est calibré à la souris/curseur dans DecorationCalibrationModal, qui affiche le
    // repère TOUJOURS centré sur le visage (jamais au-dessus/à côté) pour rester lisible
    // et prévisible — decorationTransform.position porte alors TOUT le décalage vers le
    // haut du crâne/les yeux/etc., au lieu qu'une partie soit "invisible" (déjà baked
    // dans l'ancrage aboveHead/eyes) et l'autre réglée par-dessus (constaté : le repère
    // de calibration et le point réellement utilisé en régie ne coïncidaient pas — "le
    // repère doit être centré sur le visage, pas à côté"). Un modèle de REMPLACEMENT
    // (Visage/Renard/Poisson, decorationTransform absent) garde son propre def.anchor
    // — lui n'a pas de calibration, ce comportement est inchangé pour lui.
    const modelAnchor = computeAnchorPoint(decorationTransform ? 'face' : def.anchor, pts);
    if (!leftEye || !rightEye || !noseBridge || !chin || !modelAnchor) return;

    // Position : le point d'ancrage DE CE MODÈLE (normalisé 0..1, cf. computeAnchorPoint)
    // projeté dans le plan Z=0 de la scène, à la largeur/hauteur visible par la caméra
    // à CAMERA_DISTANCE — le réglage fin calibré (cf. DecorationTransform) s'applique
    // PLUS BAS, après le lissage, pas ici.
    const vFovRad       = (CAMERA_FOV_DEG * Math.PI) / 180;
    const visibleHeight = 2 * Math.tan(vFovRad / 2) * CAMERA_DISTANCE;
    const visibleWidth  = visibleHeight * (width / height);
    const worldX = (modelAnchor.x - 0.5) * visibleWidth;
    const worldY = -(modelAnchor.y - 0.5) * visibleHeight;

    // Roulis : angle de la ligne inter-yeux — validé en régie réelle (une tête
    // penchée à droite tourne le modèle dans le sens horaire à l'écran).
    const roll = Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x);

    // Lacet (tourner la tête) et tangage (hocher la tête) : même méthode que le
    // roulis, PAS la matrice MediaPipe — on se sert de la profondeur relative (z)
    // de chaque repère, documentée par MediaPipe comme "à la même échelle que x/y",
    // plus petite (plus négative) = plus proche de la caméra. ⚠️ Nouveaux, pas
    // encore confirmés en régie réelle (contrairement au roulis) — si la tête
    // tournée fait pivoter le modèle dans le mauvais sens, inverser le signe ici.
    const yaw   = Math.atan2(rightEye.z - leftEye.z, rightEye.x - leftEye.x);
    const pitch = Math.atan2(chin.z - noseBridge.z, chin.y - noseBridge.y);

    // Échelle : taille à l'écran proportionnelle à une mesure du visage (en pixels) —
    // indépendante du choix arbitraire de CAMERA_FOV_DEG/CAMERA_DISTANCE, les deux
    // s'annulent via pixelsPerWorldUnit. targetRatio vient du modèle choisi (cf.
    // AvatarModels.ts), size de sa boîte englobante réelle (cf. loadModel).
    //
    // PAS seulement la distance inter-yeux : de face c'est une bonne mesure, mais
    // elle se rétrécit fortement en perspective dès que la tête tourne de côté (les
    // deux yeux se rapprochent à l'écran, même si le visage, lui, ne rapetisse pas
    // autant) — l'avatar rétrécissait alors et le vrai visage dépassait derrière
    // (constaté en régie réelle). Le nez-menton (vertical) résiste bien mieux à CETTE
    // rotation-là (tourner la tête ne le raccourcit presque pas) : on garde la plus
    // grande des deux mesures, robuste à un axe de rotation OU l'autre.
    const eyeDistPx     = Math.hypot((rightEye.x - leftEye.x) * width, (rightEye.y - leftEye.y) * height);
    const noseChinDistPx = Math.hypot((chin.x - noseBridge.x) * width, (chin.y - noseBridge.y) * height);
    const scaleReferencePx = Math.max(eyeDistPx, noseChinDistPx);
    const pixelsPerWorldUnit = height / visibleHeight;
    const targetPixelSize    = scaleReferencePx * def.targetRatio * scaleMultiplier;
    const worldSize          = targetPixelSize / pixelsPerWorldUnit;
    const scale = worldSize / slot.loaded.size;

    // Log de diagnostic temporaire (cf. DEBUG_SCALE) — "petit visage → grosse
    // couronne, visage agrandi → couronne rétrécit" (constaté en régie réelle) est
    // une relation INVERSE à ce que cette formule est censée produire (scale ∝
    // scaleReferencePx) ; plutôt que deviner encore une constante, on regarde les
    // vrais chiffres. À retirer une fois le réglage terminé.
    if (DEBUG_SCALE) {
      const dbgNow = performance.now();
      if (dbgNow - lastScaleDebugLogAt > 400) {
        lastScaleDebugLogAt = dbgNow;
        console.warn(
          `[Avatar échelle] id=${def.id} `
          + `scaleReferencePx=${scaleReferencePx.toFixed(1)} targetRatio=${def.targetRatio} size=${slot.loaded.size.toFixed(3)} `
          + `scale=${scale.toFixed(3)} lissée=${slot.smoothedScale.toFixed(3)} canvas=${width}x${height}`
        );
      }
    }

    // Lissage (moyenne mobile exponentielle) — atténue le bruit de mesure image par
    // image. Première frame après armement/changement de modèle POUR CE SLOT :
    // on part directement de la valeur brute, jamais de 0 (ce qui ferait glisser le
    // modèle depuis le centre de l'écran à l'apparition).
    if (!slot.hasSmoothedState) {
      slot.smoothedX = worldX; slot.smoothedY = worldY;
      slot.smoothedRoll = roll; slot.smoothedYaw = yaw; slot.smoothedPitch = pitch;
      slot.smoothedScale = scale;
      slot.hasSmoothedState = true;
    } else {
      slot.smoothedX     += (worldX - slot.smoothedX) * SMOOTHING;
      slot.smoothedY     += (worldY - slot.smoothedY) * SMOOTHING;
      slot.smoothedRoll  += (roll   - slot.smoothedRoll)  * SMOOTHING;
      slot.smoothedYaw   += (yaw    - slot.smoothedYaw)   * SMOOTHING;
      slot.smoothedPitch += (pitch  - slot.smoothedPitch) * SMOOTHING;
      slot.smoothedScale += (scale  - slot.smoothedScale) * SMOOTHING;
    }

    slot.group.position.set(slot.smoothedX, slot.smoothedY, 0);
    // Signe du pitch : REMIS à l'original (+pitch). L'inversion précédente avait été
    // faite sur un mauvais diagnostic — le "recul" observé alors venait du point
    // d'attache de la décoration mal placé (enterré dans le crâne, cf. l'ancien
    // mécanisme de décalage autonome), pas du signe. Une fois la décoration RÉELLEMENT
    // fusionnée au modèle "Visage" (cf. attachDecorationToFaceModel — plus de décalage
    // séparé à tourner), le signe inversé s'est révélé FAUX à son tour (pencher en
    // avant faisait pencher l'ensemble visage+couronne en arrière) — confirmé sur ce
    // test propre, sans plus aucune confusion possible avec un problème de décalage.
    slot.group.rotation.set(slot.smoothedPitch, slot.smoothedYaw, -slot.smoothedRoll);

    slot.group.scale.setScalar(slot.smoothedScale);

    // Réglage fin calibré (cf. DecorationTransform, panneau de calibration — icône
    // ⚙ sur chaque décoration) : un décalage LOCAL (multiples de la distance
    // inter-yeux, comme la calibration elle-même) appliqué APRÈS la pose de base,
    // tourné selon l'orientation lissée pour suivre le visage comme s'il y était
    // réellement attaché — plus une rotation et une échelle supplémentaires.
    if (decorationTransform) {
      // × scaleMultiplier (cf. AvatarEffect, curseur "Taille (visage)") : sans ça, une
      // décoration cumulée avec un modèle "Visage" agrandi/réduit restait à sa taille et
      // sa distance de décalage d'origine — visuellement plus du tout à sa place une
      // fois le visage remis à une autre taille (constaté : "si on modifie la taille du
      // visage, la couronne doit suivre").
      const eyeDistWorldUnits = (eyeDistPx / pixelsPerWorldUnit) * scaleMultiplier;
      // cf. DecorationTransform.centerDepthOffset — decorationTransform.position.z=0
      // doit tomber au CENTRE du volume de la tête RÉELLEMENT calibrée (même repère
      // que DecorationCalibrationModal), pas au plan Z=0 (racine du nez) : décalé vers
      // l'arrière AVANT la mise à l'échelle en unités monde, exactement comme
      // decorationTransform.position lui-même.
      const localOffset = new THREE.Vector3(
        decorationTransform.position[0],
        decorationTransform.position[1],
        decorationTransform.position[2] - decorationTransform.centerDepthOffset,
      ).multiplyScalar(eyeDistWorldUnits);
      const beforeRotationZ = localOffset.z;
      // Rotation COMPLÈTE (pitch+yaw+roll) — restauré : retirer le pitch (essayé
      // avant, cf. git blame) empêchait le décalage de suivre l'inclinaison réelle de
      // la tête, la décoration semblant se désolidariser/s'enfoncer dès qu'on penche
      // la tête ("tu bouges le visage donc le référentiel bouge, et tu redessines la
      // couronne par rapport à ce référentiel" — attachement rigide complet demandé
      // explicitement). Le bug de bascule vers l'avant que le retrait du pitch visait
      // à corriger tenait surtout à un `centerDepthOffset` bien trop petit (0,85→
      // maintenant ~1,5, réglable) : le vrai décalage Y était alors disproportionné
      // par rapport à un Z quasi nul, amplifiant toute imprécision de mesure du pitch.
      // Signe du pitch cohérent avec le groupe (cf. plus haut) — remis à l'original.
      localOffset.applyEuler(new THREE.Euler(slot.smoothedPitch, slot.smoothedYaw, -slot.smoothedRoll));
      slot.group.position.add(localOffset);
      slot.group.rotation.x += decorationTransform.rotation[0];
      slot.group.rotation.y += decorationTransform.rotation[1];
      slot.group.rotation.z += decorationTransform.rotation[2];
      slot.group.scale.multiply(new THREE.Vector3(...decorationTransform.scale));

      if (DEBUG_DECORATION_TRANSFORM) {
        const dbgNow = performance.now();
        if (dbgNow - (lastDecorationDebugLogAtById.get(def.id) ?? 0) > 400) {
          lastDecorationDebugLogAtById.set(def.id, dbgNow);
          // Position de la couronne PAR RAPPORT AU REPÈRE DU VISAGE (demandé
          // explicitement) — calculée de façon INDÉPENDANTE de `localOffset` ci-dessus
          // (via l'inverse du quaternion d'orientation, pas juste en relisant la
          // variable "avant rotation") : sert de vérification croisée. Si le montage
          // est cohérent, elle doit rester CONSTANTE quelle que soit l'orientation de
          // la tête — puisque c'est justement le principe d'un repère qui bouge AVEC
          // le visage (un point rigidement attaché ne bouge pas PAR RAPPORT à lui).
          const faceQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(-slot.smoothedPitch, slot.smoothedYaw, -slot.smoothedRoll));
          const worldOffsetFromAnchor = slot.group.position.clone().sub(new THREE.Vector3(slot.smoothedX, slot.smoothedY, 0));
          const positionRelativeToFaceFrame = worldOffsetFromAnchor.applyQuaternion(faceQuat.clone().invert());
          console.warn(
            `[Avatar déco] id=${def.id} `
            + `position calibrée=[${decorationTransform.position.map(v => v.toFixed(2)).join(',')}] `
            + `eyeDistWorldUnits=${eyeDistWorldUnits.toFixed(3)} `
            + `offsetLocal(avant rotation) z=${beforeRotationZ.toFixed(3)} `
            + `offsetLocal(après rotation)=[${localOffset.x.toFixed(3)},${localOffset.y.toFixed(3)},${localOffset.z.toFixed(3)}] `
            + `POSITION/repère visage=[${positionRelativeToFaceFrame.x.toFixed(3)},${positionRelativeToFaceFrame.y.toFixed(3)},${positionRelativeToFaceFrame.z.toFixed(3)}] `
            + `pitch=${(slot.smoothedPitch*180/Math.PI).toFixed(1)}° yaw=${(slot.smoothedYaw*180/Math.PI).toFixed(1)}° roll=${(slot.smoothedRoll*180/Math.PI).toFixed(1)}° `
            + `positionFinale=[${slot.group.position.x.toFixed(3)},${slot.group.position.y.toFixed(3)},${slot.group.position.z.toFixed(3)}]`
          );
        }
      }
    }

    // Log de diagnostic temporaire (cf. DEBUG_HEAD_TRACKING) — même principe que
    // DEBUG_EYE_BLINK : affiche le repère brut de MediaPipe (modelAnchor, 0..1),
    // la position monde AVANT lissage (worldX/Y) et APRÈS (smoothedX/Y), pour voir
    // si c'est MediaPipe qui ne bouge pas assez (modelAnchor quasi figé) ou notre
    // propre calcul/lissage qui écrase le mouvement (worldX/Y bouge mais pas
    // smoothedX/Y, ou pas du tout). À retirer une fois le réglage terminé.
    if (DEBUG_HEAD_TRACKING) {
      const dbgNow = performance.now();
      if (dbgNow - lastHeadTrackingDebugLogAt > 400) {
        lastHeadTrackingDebugLogAt = dbgNow;
        console.warn(
          '[Avatar tête] '
          + `repère(0..1) x=${modelAnchor.x.toFixed(3)} y=${modelAnchor.y.toFixed(3)} | `
          + `monde brut x=${worldX.toFixed(2)} y=${worldY.toFixed(2)} | `
          + `monde lissé x=${slot.smoothedX.toFixed(2)} y=${slot.smoothedY.toFixed(2)} | `
          + `échelle brute=${scale.toFixed(3)} lissée=${slot.smoothedScale.toFixed(3)} | `
          + `roulis=${(roll*180/Math.PI).toFixed(0)}° lacet=${(yaw*180/Math.PI).toFixed(0)}° tangage=${(pitch*180/Math.PI).toFixed(0)}° | `
          + `canvas ${width}x${height}`
        );
      }
    }

    // Anime le modèle s'il embarque un clip (cf. loadModel) — delta-temps entre deux
    // appels, remis à zéro au changement de modèle POUR CE SLOT. Plafonné à 100ms :
    // un onglet mis en arrière-plan puis ravivé, ou l'effet coupé puis rallumé,
    // donnerait sinon un delta énorme et ferait sauter l'animation d'un coup au
    // lieu de reprendre en douceur.
    const now = performance.now();
    const delta = slot.lastAnimTime ? Math.min((now - slot.lastAnimTime) / 1000, 0.1) : 0;
    slot.lastAnimTime = now;
    slot.loaded.mixer?.update(delta);
  }

  /** Anime TOUS les morph targets d'UN slot (cf. LoadedModel.morphTargets) depuis les
   * blendshapes MediaPipe de CE visage — clignement, sourcils, bouche, langue... tout
   * ce que le modèle chargé expose, sans liste figée d'expressions à maintenir ici.
   * No-op si le modèle chargé n'a pas de morph targets (cf. instantiate(), null).
   * Appelé en plus de updateFaceSlotTransform, jamais à sa place (position/échelle/
   * orientation globales restent gérées là-bas). */
  private applyFaceMorphTargets(slot: AnchorSlot, face: FaceTrackingResult): void {
    const morphTargets = slot.loaded?.morphTargets;
    const influences = morphTargets?.mesh.morphTargetInfluences;
    if (!morphTargets || !influences) return;

    const pts = face.landmarks;
    const leftEyeClosedness  = computeEyeClosedness(pts, LEFT_EYE_OUTER,  LEFT_EYE_UPPER_1,  LEFT_EYE_UPPER_2,  LEFT_EYE_INNER,  LEFT_EYE_LOWER_2,  LEFT_EYE_LOWER_1);
    const rightEyeClosedness = computeEyeClosedness(pts, RIGHT_EYE_OUTER, RIGHT_EYE_UPPER_1, RIGHT_EYE_UPPER_2, RIGHT_EYE_INNER, RIGHT_EYE_LOWER_2, RIGHT_EYE_LOWER_1);
    // Le secours géométrique ne sert QUE pour un clin d'œil (un œil nettement plus
    // fermé que l'autre) — un clignement des deux yeux à la fois (score MediaPipe
    // symétrique, déjà bien géré sans ce secours) ne déclenche ni l'un ni l'autre.
    const leftWink  = leftEyeClosedness  - rightEyeClosedness > EYE_ASYMMETRY_MARGIN ? leftEyeClosedness  : 0;
    const rightWink = rightEyeClosedness - leftEyeClosedness  > EYE_ASYMMETRY_MARGIN ? rightEyeClosedness : 0;

    let debugRawLeft = 0, debugRawRight = 0, debugRawJawOpen = 0;

    for (const [name, index] of Object.entries(morphTargets.indexByBlendshapeName)) {
      let raw = getBlendshapeScore(face, name);
      if (name === 'eyeBlinkLeft')  debugRawLeft  = raw;
      if (name === 'eyeBlinkRight') debugRawRight = raw;
      if (name === 'jawOpen')       debugRawJawOpen = raw;
      // Constaté dans les logs (fenêtre 21:00:24-21:00:33, œil gauche fermé en
      // continu) : le score MediaPipe brut de l'œil DROIT — pourtant grand ouvert
      // selon l'EAR (0.00 tout du long) — grimpe régulièrement à 0.4-0.6 pendant ce
      // clin d'œil. Ce n'est pas notre secours qui plisse l'œil resté ouvert, c'est
      // MediaPipe lui-même qui fait fuiter du signal entre les deux yeux pendant un
      // clin d'œil (limite du modèle public, pas un bug d'ici). Une fois qu'on est
      // SÛR que c'est un clin d'œil (asymétrie EAR nette, cf. leftWink/rightWink) :
      // l'œil qui gagne le secours l'utilise (MAX, jamais de régression) ; l'AUTRE,
      // qu'on sait donc être resté ouvert, ignore son score MediaPipe (plus fiable
      // ici) et se fie entièrement à sa propre géométrie.
      if (name === 'eyeBlinkLeft') {
        if (leftWink > 0) raw = Math.max(raw, leftWink);
        else if (rightWink > 0) raw = leftEyeClosedness;
      }
      if (name === 'eyeBlinkRight') {
        if (rightWink > 0) raw = Math.max(raw, rightWink);
        else if (leftWink > 0) raw = rightEyeClosedness;
      }
      // Lissage EMA par index, première frame pour CET index = valeur brute (jamais 0,
      // qui ferait "geler" l'expression à neutre une frame de trop à chaque apparition).
      const prev = slot.smoothedMorphTargets.get(index) ?? raw;
      const next = prev + (raw - prev) * SMOOTHING;
      slot.smoothedMorphTargets.set(index, next);
      influences[index] = next;
    }

    // Log de diagnostic temporaire (cf. DEBUG_EYE_BLINK) — affiche, pour l'œil, CHAQUE
    // étage du calcul (score MediaPipe brut, secours géométrique EAR, valeur lissée
    // réellement appliquée au modèle) pour identifier lequel des trois plafonne au
    // lieu de deviner des constantes sans pouvoir voir le résultat. À retirer une
    // fois le réglage terminé.
    if (DEBUG_EYE_BLINK) {
      const now = performance.now();
      if (now - lastEyeBlinkDebugLogAt > 400) {
        lastEyeBlinkDebugLogAt = now;
        const idxLeft    = morphTargets.indexByBlendshapeName['eyeBlinkLeft'];
        const idxRight   = morphTargets.indexByBlendshapeName['eyeBlinkRight'];
        const idxJawOpen = morphTargets.indexByBlendshapeName['jawOpen'];
        const appliedLeft    = idxLeft    !== undefined ? influences[idxLeft]    : NaN;
        const appliedRight   = idxRight   !== undefined ? influences[idxRight]   : NaN;
        const appliedJawOpen = idxJawOpen !== undefined ? influences[idxJawOpen] : NaN;
        // console.warn (pas console.log) : c'est le seul niveau relayé vers
        // files/private/logs/adminSide/remoteLog_*.txt par remoteLogger.ts.
        console.warn(
          '[Avatar œil/bouche] '
          + `œil brut L=${debugRawLeft.toFixed(2)} R=${debugRawRight.toFixed(2)} | `
          + `EAR L=${leftEyeClosedness.toFixed(2)} R=${rightEyeClosedness.toFixed(2)} | `
          + `œil appliqué L=${appliedLeft.toFixed(2)} R=${appliedRight.toFixed(2)} | `
          + `bouche brut=${debugRawJawOpen.toFixed(2)} appliqué=${appliedJawOpen.toFixed(2)}`
        );
      }
    }
  }

  /** Équivalent de updateFaceSlotTransform, mais pour un modèle ancré à une main
   * (cf. AvatarModelDef.anchor 'hand') — même principe (position/échelle/lissage/
   * animation), mais SANS lacet/tangage : aucun repère de profondeur de la main
   * n'est assez fiable pour ça (pas encore essayé, contrairement au visage où on a
   * quand même une paire œil/menton stable). Roulis seulement, depuis l'axe
   * poignet→base du majeur (l'équivalent, pour la main, de la ligne inter-yeux du
   * visage) — pas encore confirmé en régie réelle (aucun banc de test disponible ici). */
  private updateHandSlotTransform(slot: AnchorSlot, hand: HandTrackingResult, width: number, height: number, def: AvatarModelDef, scaleMultiplier: number, anchorLandmarkIndex?: number, decorationTransform?: DecorationTransform): void {
    if (!slot.loaded) return;
    const pts    = hand.landmarks;
    const wrist  = pts[WRIST];
    const middle = pts[MIDDLE_FINGER_MCP];
    const index  = pts[INDEX_FINGER_MCP];
    const pinky  = pts[PINKY_MCP];
    // Un doigt précis (cf. FINGER_ANCHOR_LANDMARK, AvatarEffect "Bague") plutôt que le
    // centre générique de la main — sert par ex. à poser une bague sur UNE articulation
    // précise au lieu du dos de la main.
    const anchor = anchorLandmarkIndex !== undefined ? (pts[anchorLandmarkIndex] ?? null) : computeHandAnchorPoint(pts);
    if (!wrist || !middle || !index || !pinky || !anchor) return;

    const vFovRad       = (CAMERA_FOV_DEG * Math.PI) / 180;
    const visibleHeight = 2 * Math.tan(vFovRad / 2) * CAMERA_DISTANCE;
    const visibleWidth  = visibleHeight * (width / height);
    const worldX = (anchor.x - 0.5) * visibleWidth;
    const worldY = -(anchor.y - 0.5) * visibleHeight;

    // Roulis : angle de l'axe poignet→majeur par rapport à la verticale de l'écran
    // (0 = main dressée, doigts vers le haut) — même logique que le roulis du
    // visage (angle d'une ligne stable du repère suivi), transposée à la main.
    const roll = Math.atan2(middle.x - wrist.x, wrist.y - middle.y);

    const palmWidthPx       = Math.hypot((index.x - pinky.x) * width, (index.y - pinky.y) * height);
    const pixelsPerWorldUnit = height / visibleHeight;
    const targetPixelSize    = palmWidthPx * def.targetRatio * scaleMultiplier;
    const worldSize          = targetPixelSize / pixelsPerWorldUnit;
    const scale = worldSize / slot.loaded.size;

    if (!slot.hasSmoothedState) {
      slot.smoothedX = worldX; slot.smoothedY = worldY;
      slot.smoothedRoll = roll; slot.smoothedYaw = 0; slot.smoothedPitch = 0;
      slot.smoothedScale = scale;
      slot.hasSmoothedState = true;
    } else {
      slot.smoothedX     += (worldX - slot.smoothedX) * SMOOTHING;
      slot.smoothedY     += (worldY - slot.smoothedY) * SMOOTHING;
      slot.smoothedRoll  += (roll   - slot.smoothedRoll)  * SMOOTHING;
      slot.smoothedScale += (scale  - slot.smoothedScale) * SMOOTHING;
    }

    slot.group.position.set(slot.smoothedX, slot.smoothedY, 0);
    slot.group.rotation.set(0, 0, -slot.smoothedRoll);
    slot.group.scale.setScalar(slot.smoothedScale);

    // Réglage fin calibré (cf. DecorationTransform) — même principe que pour le
    // visage (cf. updateFaceSlotTransform), mais en multiples de la largeur de
    // paume plutôt que de la distance inter-yeux.
    if (decorationTransform) {
      // × scaleMultiplier (cf. AvatarEffect, curseur "Taille (mains)") — même raison
      // que updateFaceSlotTransform.
      const palmWidthWorldUnits = (palmWidthPx / pixelsPerWorldUnit) * scaleMultiplier;
      // cf. DecorationTransform.centerDepthOffset — même principe que updateFaceSlotTransform.
      const localOffset = new THREE.Vector3(
        decorationTransform.position[0],
        decorationTransform.position[1],
        decorationTransform.position[2] - decorationTransform.centerDepthOffset,
      ).multiplyScalar(palmWidthWorldUnits);
      localOffset.applyEuler(new THREE.Euler(0, 0, -slot.smoothedRoll));
      slot.group.position.add(localOffset);
      slot.group.rotation.x += decorationTransform.rotation[0];
      slot.group.rotation.y += decorationTransform.rotation[1];
      slot.group.rotation.z += decorationTransform.rotation[2];
      slot.group.scale.multiply(new THREE.Vector3(...decorationTransform.scale));
    }

    const now = performance.now();
    const delta = slot.lastAnimTime ? Math.min((now - slot.lastAnimTime) / 1000, 0.1) : 0;
    slot.lastAnimTime = now;
    slot.loaded.mixer?.update(delta);
  }

  /** Repositionne/réoriente INDIVIDUELLEMENT chaque os et articulation du rig (cf.
   * AvatarModelDef.handRigBuild, AvatarProceduralModels.HandRigParts) depuis les 21
   * repères RÉELS de CETTE main — contrairement à updateHandSlotTransform (un seul
   * bloc rigide), chaque phalange suit sa propre paire de repères. Les positions sont
   * calculées directement en unités monde (même projection FOV/distance que pour un
   * modèle rigide, appliquée à CHAQUE repère plutôt qu'au seul point d'ancrage) — pas
   * de targetRatio/boîte englobante ici, la taille vient de la main réelle mesurée.
   * Profondeur (z) volontairement ignorée pour l'instant : tout est posé sur un même
   * plan face caméra — une main tournée en biais n'aura donc pas encore le bon relief
   * (amélioration possible, pas faite faute de pouvoir la vérifier ici). */
  private updateHandRigTransform(slot: AnchorSlot, hand: HandTrackingResult, width: number, height: number, scaleMultiplier: number): void {
    const handRig = slot.loaded?.handRig;
    if (!handRig) return;
    const pts = hand.landmarks;
    const wrist = pts[WRIST], indexMcp = pts[INDEX_FINGER_MCP], pinkyMcp = pts[PINKY_MCP];
    if (!wrist || !indexMcp || !pinkyMcp) return;

    const vFovRad       = (CAMERA_FOV_DEG * Math.PI) / 180;
    const visibleHeight = 2 * Math.tan(vFovRad / 2) * CAMERA_DISTANCE;
    const visibleWidth  = visibleHeight * (width / height);
    const toWorldRaw = (p: HandLandmarkPoint) => new THREE.Vector3((p.x - 0.5) * visibleWidth, -(p.y - 0.5) * visibleHeight, 0);
    const wristWorld = toWorldRaw(wrist);
    // "Taille" (scaleMultiplier) grossit/rétrécit la main entière AUTOUR du poignet —
    // pas seulement l'épaisseur des os — pour que le réglage ait un effet visible
    // cohérent (cf. AvatarEffect, LevelSlider "Taille").
    const toWorld = (p: HandLandmarkPoint) => wristWorld.clone().add(toWorldRaw(p).sub(wristWorld).multiplyScalar(scaleMultiplier));

    const palmWidthWorld = toWorld(indexMcp).distanceTo(toWorld(pinkyMcp));
    // Rayon de base commun aux trois styles — le détail (paume plus large, doigts
    // qui s'affinent, articulations plus ou moins noueuses) vient des facteurs
    // rebakés par style dans buildHandRigParts (cf. bone.radiusFactor,
    // handRig.jointRadiusRatio), pas d'ici.
    const boneRadius  = palmWidthWorld * HAND_RIG_BONE_RADIUS_RATIO;
    const jointRadius = boneRadius * handRig.jointRadiusRatio;

    // Distinction visuelle gauche/droite (cf. HAND_RIG_COLOR_LEFT/RIGHT) — un seul
    // set() par frame sur la matière PARTAGÉE du rig, pas par maillage individuel.
    handRig.material.color.setHex(hand.handedness === 'Left' ? HAND_RIG_COLOR_LEFT : HAND_RIG_COLOR_RIGHT);

    // Le groupe racine reste à l'identité — chaque os/articulation est déjà en
    // coordonnées MONDE (calculées ci-dessus), pas besoin d'un pivot global ici.
    slot.group.position.set(0, 0, 0);
    slot.group.rotation.set(0, 0, 0);
    slot.group.scale.setScalar(1);

    for (const bone of handRig.bones) {
      const a = pts[bone.startIdx], b = pts[bone.endIdx];
      if (!a || !b) { bone.mesh.visible = false; continue; }
      bone.mesh.visible = true;
      const wa = toWorld(a), wb = toWorld(b);
      const mid = wa.clone().add(wb).multiplyScalar(0.5);
      const length = Math.max(wa.distanceTo(wb), 0.001);
      const radius = boneRadius * bone.radiusFactor;
      bone.mesh.position.copy(mid);
      bone.mesh.scale.set(radius, length, radius);
      const dir = wb.clone().sub(wa).normalize();
      bone.mesh.quaternion.setFromUnitVectors(HAND_RIG_UP_AXIS, dir);
    }

    for (const joint of handRig.joints) {
      const p = pts[joint.index];
      if (!p) { joint.mesh.visible = false; continue; }
      joint.mesh.visible = true;
      joint.mesh.position.copy(toWorld(p));
      joint.mesh.scale.setScalar(joint.index === HAND_RIG_WRIST_INDEX ? jointRadius * 1.4 : jointRadius);
    }
  }

  /** Complète les détections RÉELLES de cette frame (`live`) avec la dernière pose
   * connue des slots existants au-delà de `live.length` — tant qu'elle n'a pas
   * dépassé ANCHOR_HOLD_MS. S'arrête au premier slot sans pose tenable (expirée ou
   * jamais vue) plutôt que de laisser un trou dans le tableau : les slots suivants,
   * eux, sont bel et bien masqués par l'appelant (cf. render()). */
  private mergeWithHeldAnchors<T extends FaceTrackingResult | HandTrackingResult>(slots: AnchorSlot[], live: T[], now: number): T[] {
    const merged = live.slice();
    for (let i = live.length; i < slots.length; i++) {
      const slot = slots[i];
      if (slot.lastKnownAnchor && now - slot.lastSeenAt <= ANCHOR_HOLD_MS) {
        merged.push(slot.lastKnownAnchor as T);
      } else {
        break;
      }
    }
    return merged;
  }

  /** Commun aux deux familles (visage/mains, cf. AvatarEffect) : tient à jour les
   * slots d'UNE famille pour cette frame — grandit/masque au besoin, tient la
   * dernière pose connue (cf. mergeWithHeldAnchors), et appelle `updateItem` pour
   * chaque repère prêt (position/rotation/animation propres à la famille — visage
   * ou main, cf. render()). Retourne les index en attente de chargement de modèle
   * (repli points de repère côté appelant) ; ne fait RIEN et rend tous les slots de
   * cette famille invisibles si `def` est null (famille désactivée). */
  private updateFamily<T extends FaceTrackingResult | HandTrackingResult>(
    slots: AnchorSlot[], live: T[], def: AvatarModelDef | null, now: number,
    // Index transmis (cf. updateDecorations, modèle fusionné) : pour une décoration
    // ancrée au visage, c'est le MÊME index que celui du visage suivi dans
    // this.faceSlots (les deux itèrent le même tableau facesLandmarks) — sert à
    // retrouver le bon modèle "Visage" auquel se fusionner.
    updateItem: (slot: AnchorSlot, item: T, index: number) => void,
  ): number[] {
    if (!def) {
      for (const slot of slots) slot.group.visible = false;
      return [];
    }

    const anchors = this.mergeWithHeldAnchors(slots, live, now);
    const activeSlots: AnchorSlot[] = [];
    for (let i = 0; i < anchors.length; i++) {
      const slot = this.ensureSlot(slots, i);
      this.ensureSlotModel(slot, def);
      activeSlots.push(slot);
    }
    for (let i = anchors.length; i < slots.length; i++) slots[i].group.visible = false;

    const pendingIndices: number[] = [];
    for (let i = 0; i < anchors.length; i++) {
      const slot = activeSlots[i];
      slot.group.visible = !!slot.loaded;
      if (!slot.loaded) { pendingIndices.push(i); continue; }
      updateItem(slot, anchors[i], i);
      // N'arme/rafraîchit la pose tenue QUE sur une détection réelle de cette frame —
      // jamais en rejouant une pose déjà tenue, sinon le délai ne s'épuiserait jamais.
      if (i < live.length) { slot.lastKnownAnchor = anchors[i]; slot.lastSeenAt = now; }
    }
    return pendingIndices;
  }

  /** FUSIONNE une décoration ancrée au visage dans le modèle de remplacement "Visage"
   * RÉELLEMENT actif (facecap, renard...), quand il y en a un — demandé explicitement
   * ("génère un 3ème modèle, fusion des deux premiers... il n'y a plus de raison que
   * la couronne bouge, c'est un nouveau modèle qui bouge") plutôt que de continuer à
   * calculer sa position/rotation séparément (par un décalage tourné autour d'un
   * pivot APPROXIMATIF — racine du nez + une demi-profondeur ESTIMÉE, cf.
   * DecorationTransform.centerDepthOffset). Ici, `decoSlot.group` devient un enfant
   * RÉEL de `faceSlot.group` (Object3D.add() détache automatiquement de l'ancien
   * parent, scène ou autre) : sa rotation vient alors ENTIÈREMENT du parent (plus rien
   * à additionner à la main), et sa position se mesure par rapport au vrai CENTRE
   * MESURÉ de ce modèle précis (cf. LoadedModel.centerOffsetFromPivot) — plus aucune
   * estimation générique de profondeur de tête nécessaire pour ce cas. Ne s'applique
   * que si un modèle "Visage" est actif ET chargé pour CE visage précis — sinon (cf.
   * updateDecorations) la décoration reste sur son mécanisme autonome existant. */
  private attachDecorationToFaceModel(
    decoSlot: AnchorSlot, faceSlot: AnchorSlot, decoDef: AvatarModelDef, faceDef: AvatarModelDef,
    transform: DecorationTransform, decoScaleMultiplier: number, faceScaleMultiplier: number,
  ): void {
    const faceLoaded = faceSlot.loaded!;
    const decoLoaded = decoSlot.loaded!;
    faceSlot.group.add(decoSlot.group);

    // Unités calibrées (distance inter-yeux) → unités NATIVES du modèle de visage
    // (son espace local, avant la mise à l'échelle que porte déjà faceSlot.group —
    // héritée automatiquement puisque decoSlot.group en est maintenant un enfant).
    // ÷ GENERIC_HEAD_HEIGHT (PAS faceDef.targetRatio, jamais vérifié sur un vrai
    // visage, essayé avant) — la MÊME règle que DecorationCalibrationModal utilise
    // pour recaler sa référence affichée (÷ heightY, PAS `size` — sa plus grande
    // dimension, potentiellement pas l'axe Y) : deux règles séparées avaient fini par
    // diverger, un réglage à grand décalage (lunettes) le révélait, un petit
    // (couronne) le masquait (constaté).
    const unitsPerEyeDistance = faceLoaded.heightY / GENERIC_HEAD_HEIGHT;
    decoSlot.group.position.copy(faceLoaded.centerOffsetFromPivot)
      .add(new THREE.Vector3(...transform.position).multiplyScalar(unitsPerEyeDistance));
    decoSlot.group.rotation.set(transform.rotation[0], transform.rotation[1], transform.rotation[2]);

    // Échelle MONDE visée pour cette décoration ÷ échelle déjà portée par le parent —
    // sans le facteur eyeDistWorldUnits (mesure réelle par frame) : il s'annule
    // algébriquement entre le calcul de la taille visée et celui de l'échelle du
    // parent (les deux la contiennent, cf. commentaire de LoadedModel.
    // centerOffsetFromPivot) — donc pas besoin de la reporter ici.
    const k = (faceLoaded.size * decoDef.targetRatio * decoScaleMultiplier)
      / (faceDef.targetRatio * faceScaleMultiplier * decoLoaded.size);
    decoSlot.group.scale.set(
      k * transform.scale[0], k * transform.scale[1], k * transform.scale[2],
    );

    if (DEBUG_DECORATION_TRANSFORM) {
      const dbgNow = performance.now();
      if (dbgNow - (lastDecorationDebugLogAtById.get(decoDef.id) ?? 0) > 400) {
        lastDecorationDebugLogAtById.set(decoDef.id, dbgNow);
        console.warn(
          `[Avatar déco fusionné] id=${decoDef.id} `
          + `position calibrée=[${transform.position.map(v => v.toFixed(2)).join(',')}] `
          + `centerOffsetFromPivot=[${faceLoaded.centerOffsetFromPivot.x.toFixed(3)},${faceLoaded.centerOffsetFromPivot.y.toFixed(3)},${faceLoaded.centerOffsetFromPivot.z.toFixed(3)}] `
          + `unitsPerEyeDistance=${unitsPerEyeDistance.toFixed(3)} `
          + `positionLocale=[${decoSlot.group.position.x.toFixed(3)},${decoSlot.group.position.y.toFixed(3)},${decoSlot.group.position.z.toFixed(3)}] `
          + `k=${k.toFixed(3)} decoLoaded.size=${decoLoaded.size.toFixed(3)} faceLoaded.size=${faceLoaded.size.toFixed(3)}`
        );
      }
    }
  }

  /** Même principe que attachDecorationToFaceModel, mais pour une décoration ancrée à
   * la MAIN (ex. Bague) — fusionnée à la bonne PHALANGE (cf. `boneMesh`, choisie par
   * l'appelant via FINGER_PIP_LANDMARK) du rig de main ('Mains' réglé sur un des
   * styles articulés), demandé explicitement plutôt que rester sur l'ancien mécanisme
   * autonome (décalage tourné seulement en roulis, aucun suivi de l'orientation
   * PROPRE de CETTE phalange quand le doigt se plie). Différence avec le visage :
   * l'échelle du bone (rayon, longueur, rayon) est ANISOTROPE et vient d'une MESURE
   * RÉELLE par frame (longueur de la phalange), pas d'un targetRatio fixe — aucune
   * simplification algébrique possible, `palmWidthWorldUnits` doit être fourni par
   * l'appelant (déjà calculé pour cette frame). */
  private attachDecorationToHandBone(
    decoSlot: AnchorSlot, boneMesh: THREE.Object3D, decoDef: AvatarModelDef,
    transform: DecorationTransform, decoScaleMultiplier: number, palmWidthWorldUnits: number,
  ): void {
    const decoLoaded = decoSlot.loaded;
    if (!decoLoaded) return;
    boneMesh.add(decoSlot.group);

    decoSlot.group.position.set(
      (transform.position[0] * palmWidthWorldUnits) / boneMesh.scale.x,
      (transform.position[1] * palmWidthWorldUnits) / boneMesh.scale.y,
      (transform.position[2] * palmWidthWorldUnits) / boneMesh.scale.z,
    );
    decoSlot.group.rotation.set(transform.rotation[0], transform.rotation[1], transform.rotation[2]);

    const worldSize = (i: number) => (palmWidthWorldUnits * decoDef.targetRatio * decoScaleMultiplier * transform.scale[i]) / decoLoaded.size;
    decoSlot.group.scale.set(
      worldSize(0) / boneMesh.scale.x,
      worldSize(1) / boneMesh.scale.y,
      worldSize(2) / boneMesh.scale.z,
    );
  }

  private getDecorationSlots(decorationId: string): AnchorSlot[] {
    let slots = this.decorationSlots.get(decorationId);
    if (!slots) {
      slots = [];
      this.decorationSlots.set(decorationId, slots);
    }
    return slots;
  }

  /** Met à jour TOUTES les décorations actives (cf. AvatarModelDef.isDecoration) —
   * chacune a son propre jeu de slots (cf. decorationSlots), ancré au visage ou à
   * la main selon SON PROPRE `anchor` (une décoration 'hand', ex. Bague, suit les
   * mains même si aucun modèle "Mains" n'est choisi par ailleurs). Une décoration
   * précédemment active mais absente de `decorationIds` cette frame voit ses slots
   * masqués (pas détruits — cf. decorationSlots, réutilisés si recochée).
   *
   * `handOptionsById` : réglages fins UNIQUEMENT pertinents pour une décoration
   * ancrée 'hand' (ex. Bague — quelle main, quel doigt, cf. AvatarEffect) — un id
   * absent de cette map utilise le comportement par défaut (toutes les mains,
   * ancrage générique au dos de la main). Générique côté renderer : il ne connaît
   * pas "la Bague" par son nom, juste des options éventuelles par id. */
  private updateDecorations(
    decorationIds: string[], facesLandmarks: FaceTrackingResult[], handsLandmarks: HandTrackingResult[],
    width: number, height: number, now: number, handOptionsById: Map<string, HandDecorationOptions>,
    decorationTransformsById: Map<string, DecorationTransform>,
    faceScaleMultiplier: number, handScaleMultiplier: number,
    // Modèle "Visage" actuellement actif (peut être null) — cf. attachDecorationToFaceModel :
    // une décoration ancrée au visage s'y fusionne QUAND il est chargé pour CE visage.
    faceDef: AvatarModelDef | null,
  ): Map<string, number[]> {
    const activeIds = new Set(decorationIds);
    // .forEach (pas for...of) : la cible ES5 du projet n'itère pas nativement une
    // Map sans downlevelIteration — Map.forEach, lui, ne dépend pas du protocole
    // itérateur.
    this.decorationSlots.forEach((slots, id) => {
      if (!activeIds.has(id)) { for (const slot of slots) slot.group.visible = false; }
    });

    const pendingByDecoration = new Map<string, number[]>();
    for (const id of decorationIds) {
      const def = getAvatarModelDef(id);
      if (!def) continue;
      const slots = this.getDecorationSlots(id);
      let pending: number[];
      if (def.anchor === 'hand') {
        const options = handOptionsById.get(id);
        // 'both' (ou pas d'option) : toutes les mains détectées, comme avant.
        // 'left'/'right' : ne garde QUE les mains de ce côté (cf. HandTrackingResult.
        // handedness) — les autres n'ont simplement pas de slot actif cette frame.
        const hands = !options?.handSide || options.handSide === 'both'
          ? handsLandmarks
          : handsLandmarks.filter(h => (h.handedness === 'Left' ? 'left' : 'right') === options.handSide);
        const transform = decorationTransformsById.get(id) ?? DEFAULT_DECORATION_TRANSFORM;
        pending = this.updateFamily(slots, hands, def, now, (slot, hand) => {
          if (def.handRigBuild) { this.updateHandRigTransform(slot, hand, width, height, handScaleMultiplier); return; }

          // Fusion à la bonne PHALANGE du rig de main "Mains" ('hand-rig-*'), quand il
          // est actif et chargé — demandé explicitement, même principe que la fusion
          // au visage. `handsLandmarks.indexOf(hand)` (pas l'index dans `hands`, qui
          // peut être FILTRÉ par main gauche/droite — cf. ci-dessus) retrouve le bon
          // index dans this.handSlots, construit lui sur le tableau NON filtré. -1 (pose
          // TENUE d'une frame précédente, absente du tableau courant, cf.
          // mergeWithHeldAnchors) : repli sur le mécanisme autonome ci-dessous.
          // (Depuis que les mains ont une réserve de slots par côté, il n'y a plus d'index
          // unique, et plus aucun modèle "rig" n'est proposé : pas de fusion à un os.)
          const handRig = undefined as HandRigParts | undefined;
          const targetLandmark = options?.fingerLandmarkIndex;
          const boneIndex = targetLandmark !== undefined ? HAND_RIG_BONES.findIndex(([, endIdx]) => endIdx === targetLandmark) : -1;
          const bone = handRig && boneIndex >= 0 ? handRig.bones[boneIndex] : undefined;
          if (bone) {
            const vFovRad = (CAMERA_FOV_DEG * Math.PI) / 180;
            const visibleHeight = 2 * Math.tan(vFovRad / 2) * CAMERA_DISTANCE;
            const pixelsPerWorldUnit = height / visibleHeight;
            const indexMcp = hand.landmarks[INDEX_FINGER_MCP], pinkyMcp = hand.landmarks[PINKY_MCP];
            const palmWidthPx = Math.hypot((indexMcp.x - pinkyMcp.x) * width, (indexMcp.y - pinkyMcp.y) * height);
            const palmWidthWorldUnits = palmWidthPx / pixelsPerWorldUnit;
            this.attachDecorationToHandBone(slot, bone.mesh, def, transform, handScaleMultiplier, palmWidthWorldUnits);
            return;
          }
          this.scene.add(slot.group);
          this.updateHandSlotTransform(slot, hand, width, height, def, handScaleMultiplier, options?.fingerLandmarkIndex, transform);
        });
      } else {
        const transform = decorationTransformsById.get(id) ?? DEFAULT_DECORATION_TRANSFORM;
        pending = this.updateFamily(slots, facesLandmarks, def, now, (slot, face, index) => {
          const faceSlot = this.faceSlots[index];
          if (faceDef && faceSlot?.loaded) {
            this.attachDecorationToFaceModel(slot, faceSlot, def, faceDef, transform, faceScaleMultiplier, faceScaleMultiplier);
            return;
          }
          // Pas (ou plus) de modèle "Visage" chargé pour ce visage précis — mécanisme
          // autonome existant (decalage + rotation manuelle depuis les repères suivis).
          // this.scene.add() redétache proprement si la décoration était fusionnée
          // l'instant d'avant (visage désactivé entre deux frames).
          this.scene.add(slot.group);
          this.updateFaceSlotTransform(slot, face, width, height, def, faceScaleMultiplier, transform);
          this.applyFaceMorphTargets(slot, face);
        });
      }
      pendingByDecoration.set(id, pending);
    }
    return pendingByDecoration;
  }

  /**
   * Rend, sur UN MÊME canvas hors-écran (fond transparent), le modèle "visage"
   * choisi (ancré à CHAQUE visage suivi), le modèle "mains" choisi (ancré à CHAQUE
   * main suivie), ET toutes les décorations cochées (cf. updateDecorations) — les
   * trois familles sont INDÉPENDANTES et cumulables (cf. AvatarEffect) : chacune
   * peut être vide/absente sans affecter les autres. Résultat prêt à être composé
   * par-dessus la frame — cf. AvatarEffect.composite().
   *
   * Chaque visage/main est indépendant(e) au sein de sa famille : celui/celle dont
   * le modèle est déjà chargé s'affiche en 3D, celui/celle qui charge encore (ou
   * vient de changer de modèle) est listé(e) dans son `pendingIndices` — à
   * l'appelant de poser son repli (points de repère) UNIQUEMENT pour ceux-là.
   * Un visage/une main qui disparaît brièvement de la détection (rotation trop
   * marquée, cf. ANCHOR_HOLD_MS) continue de s'afficher, figé sur sa dernière pose
   * connue — cf. mergeWithHeldAnchors. Retourne null seulement si le contexte
   * WebGL est indisponible.
   */
  render(
    facesLandmarks: FaceTrackingResult[], handsLandmarks: HandTrackingResult[],
    width: number, height: number,
    faceModelId: string | null, faceScaleMultiplier: number,
    leftHandModelId: string | null, rightHandModelId: string | null, handScaleMultiplier: number,
    decorationIds: string[], handOptionsById: Map<string, HandDecorationOptions>,
    decorationTransformsById: Map<string, DecorationTransform>,
  ): { canvas: HTMLCanvasElement; facePendingIndices: number[]; handPendingIndices: number[]; decorationPendingIndices: Map<string, number[]> } | null {
    const renderer = this.ensureRenderer(width, height);
    if (!renderer) return null;

    const now = performance.now();
    const faceDef = getAvatarModelDef(faceModelId);
    const leftHandDef  = getAvatarModelDef(leftHandModelId);
    const rightHandDef = getAvatarModelDef(rightHandModelId);

    const facePendingIndices = this.updateFamily(this.faceSlots, facesLandmarks, faceDef, now, (slot, face) => {
      this.updateFaceSlotTransform(slot, face, width, height, faceDef!, faceScaleMultiplier);
      this.applyFaceMorphTargets(slot, face);
    });

    // Gauche et droite sont deux familles séparées, chacune avec son modèle et sa
    // réserve de slots. Les index « en attente » sont ramenés à ceux du tableau complet
    // des mains (c'est ce que l'appelant utilise pour son repli points de repère) ; une
    // pose tenue, absente de la frame, n'y a pas d'index et n'a rien à dessiner.
    const leftHands  = handsLandmarks.filter(h => isPersonLeftHand(h));
    const rightHands = handsLandmarks.filter(h => !isPersonLeftHand(h));
    const updateSide = (slots: AnchorSlot[], hands: HandTrackingResult[], def: AvatarModelDef | null): number[] =>
      this.updateFamily(slots, hands, def, now, (slot, hand) => {
        if (def!.handRigBuild) {
          this.updateHandRigTransform(slot, hand, width, height, handScaleMultiplier);
        } else {
          this.updateHandSlotTransform(slot, hand, width, height, def!, handScaleMultiplier);
        }
      }).filter(i => i < hands.length).map(i => handsLandmarks.indexOf(hands[i]));
    const handPendingIndices = [
      ...updateSide(this.leftHandSlots,  leftHands,  leftHandDef),
      ...updateSide(this.rightHandSlots, rightHands, rightHandDef),
    ];

    const decorationPendingIndices = this.updateDecorations(decorationIds, facesLandmarks, handsLandmarks, width, height, now, handOptionsById, decorationTransformsById, faceScaleMultiplier, handScaleMultiplier, faceDef);

    renderer.render(this.scene, this.camera);
    return { canvas: this.canvas, facePendingIndices, handPendingIndices, decorationPendingIndices };
  }

  dispose(): void {
    this.renderer?.dispose();
  }
}

// Un AvatarRenderer par SOURCE (indexé sur son élément <video>, stable pour toute la
// durée de vie de la source — cf. `frame` dans VideoEffectCompositeArgs), pas un
// singleton global unique. La régie peut avoir l'effet Avatar actif sur plusieurs
// sources en même temps, chacune avec son propre modèle choisi ; comme
// loadModel().then() est TOUJOURS asynchrone même déjà en cache (une promesse résolue
// ne rappelle jamais son .then() de façon synchrone), un seul renderer partagé entre
// deux sources qui alterneraient entre deux modèles différents ne se stabiliserait
// jamais — chaque frame invaliderait le changement de modèle de l'autre avant qu'il
// n'ait eu le temps de se monter. Un WeakMap : pas de nettoyage à faire à la main
// quand une source disparaît, l'entrée part avec son <video>.
const renderersBySource = new WeakMap<HTMLVideoElement, AvatarRenderer>();

/** Renderer de CETTE source — le contexte WebGL n'est créé qu'au premier rendu réel. */
export function getAvatarRenderer(frame: HTMLVideoElement): AvatarRenderer {
  let renderer = renderersBySource.get(frame);
  if (!renderer) {
    renderer = new AvatarRenderer();
    renderersBySource.set(frame, renderer);
  }
  return renderer;
}

/**
 * Libère explicitement le contexte WebGL de CETTE source, sans attendre que son
 * élément <video> soit ramassé par le GC (cf. le commentaire de la WeakMap
 * ci-dessus — un délai non déterministe, risqué si des sources créent/détruisent
 * leur pipeline effet vidéo à répétition : un navigateur ne tolère qu'une poignée
 * de contextes WebGL simultanés). À appeler dès qu'on sait qu'une source n'aura
 * plus besoin de son AvatarRenderer (pipeline détruit, effet désactivé). Sans
 * effet si aucun renderer n'existait déjà pour cet élément.
 */
export function disposeAvatarRenderer(frame: HTMLVideoElement): void {
  const renderer = renderersBySource.get(frame);
  if (!renderer) return;
  renderer.dispose();
  renderersBySource.delete(frame);
}

// ── Miniatures des tuiles ───────────────────────────────────────────────────────
// Un rendu fixe (face, légèrement de trois-quarts) du modèle, fait UNE fois par modèle
// dans un petit canvas WebGL partagé, puis gardé en mémoire sous forme d'image. Les
// rendus passent l'un après l'autre : un navigateur ne tolère qu'une poignée de
// contextes WebGL, et celui-ci doit rester unique.
const THUMB_SIZE = 128;
const thumbnailCache = new Map<string, Promise<string | null>>();
let thumbnailQueue: Promise<unknown> = Promise.resolve();
let thumbnailRenderer: THREE.WebGLRenderer | null = null;

function renderThumbnail(template: ModelTemplate): string {
  if (!thumbnailRenderer) {
    thumbnailRenderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
    thumbnailRenderer.setSize(THUMB_SIZE, THUMB_SIZE, false);
    thumbnailRenderer.setClearColor(0x000000, 0);
  }
  const scene = new THREE.Scene();
  const model = cloneSkeleton(template.scene) as THREE.Object3D;
  scene.add(model);
  scene.add(new THREE.AmbientLight(0xffffff, 1.6));
  const key = new THREE.DirectionalLight(0xffffff, 2.2);
  key.position.set(1, 1.5, 2);
  scene.add(key);

  // Cadrage d'après la boîte englobante réelle du clone (déjà à l'échelle du fichier).
  const box = new THREE.Box3().setFromObject(model);
  const center = box.getCenter(new THREE.Vector3());
  const radius = box.getSize(new THREE.Vector3()).length() / 2 || 1;
  const camera = new THREE.PerspectiveCamera(30, 1, radius / 100, radius * 100);
  const distance = radius / Math.sin((camera.fov * Math.PI) / 360);
  camera.position.copy(center).add(new THREE.Vector3(0.35, 0.2, 1).normalize().multiplyScalar(distance));
  camera.lookAt(center);

  thumbnailRenderer.render(scene, camera);
  return thumbnailRenderer.domElement.toDataURL('image/png');
}

/** Miniature (data URL PNG) d'un modèle glTF, ou null si impossible. Jamais pour un
 * modèle généré en code ou un rig de main (rien à afficher d'autre que l'icône). */
export function getModelThumbnail(def: AvatarModelDef): Promise<string | null> {
  if (def.build || def.handRigBuild) return Promise.resolve(null);
  let cached = thumbnailCache.get(def.id);
  if (!cached) {
    cached = new Promise<string | null>(resolve => {
      thumbnailQueue = thumbnailQueue.then(async () => {
        try {
          resolve(renderThumbnail(await loadTemplate(def)));
        } catch (err) {
          console.warn('[AVATAR] miniature impossible pour', def.id, err);
          resolve(null);
        }
      });
    });
    thumbnailCache.set(def.id, cached);
  }
  return cached;
}
